import { useEffect, useMemo, useState } from "react";

import { useHistory, useParams } from "react-router-dom";

import { deleteMenuItem, updateMenuItem } from "services/menu/rest";
import {
    ExternalProductDto,
    MediaItemDto,
    ModifierSetRefDto,
    ScheduleDto,
} from "services/menu/restModels";
import { UpdateMenuItemRequestDto } from "services/menu/restRequests";
import { useModifierSetMutations } from "features/ModifierSets/hooks/useModifierSetMutations";
import { useConstants } from "shared/hooks/useConstants";
import { useCurrencyId } from "shared/hooks/useCurrencyId";
import { useLocale } from "shared/hooks/useLocale";
import { useMenuCategories } from "shared/hooks/useMenuCategories";
import { useMenuItem } from "shared/hooks/useMenuItem";
import { useMenuItemBadges } from "shared/hooks/useMenuItemBadges";
import { useMenuSettings } from "shared/hooks/useMenuSettings";
import { useModifierSets } from "shared/hooks/useModifierSets";
import {
    createDecimalInputHandler,
    createMoneyInputHandler,
    formatDecimalForInput,
} from "shared/lib/decimalInput";
import { createCurrencyFormatter } from "shared/lib/currencyFormatter";
import { useNotificationSnackbar } from "shared/ui";
import { notifyOnPage } from "shared/hooks/usePageNotification";

// Store-less состояние редактора: позиция, константы, наборы модификаторов, настройки
// меню и его категории — всё из REST.
export const useMenuItemEditorState = () => {
    const history = useHistory();
    const params = useParams<{ menuId: string; categoryId: string; menuItemId: string }>();
    const menuId = parseInt(params.menuId);
    const categoryId = parseInt(params.categoryId);
    const menuItemId = parseInt(params.menuItemId);


    const locale = useLocale();
    const currencyId = useCurrencyId();

    const { item, loading: itemLoading, error: itemError } = useMenuItem(menuItemId);
    const { constants, loading: constantsLoading, error: constantsError } = useConstants();
    const {
        settings: menuSettings,
        loading: menuLoading,
        error: menuError,
    } = useMenuSettings(menuId);
    const {
        categories,
        loading: categoriesLoading,
        error: categoriesError,
    } = useMenuCategories(menuId);
    const {
        badges: badgesCatalog,
        loading: badgesLoading,
        error: badgesError,
    } = useMenuItemBadges();
    const {
        modifierSets: modifierSetsCatalog,
        loading: modifierSetsLoading,
        error: modifierSetsError,
        setModifierSets: setModifierSetsCatalog,
    } = useModifierSets();

    const currency = constants?.currencies.find((it) => it.id === currencyId) || null;
    const currencyFormatter = useMemo(
        () => createCurrencyFormatter(currency, locale),
        [currency, locale]
    );

    // Каталог наборов нужен только вкладке «Модификаторы» — ни загрузка, ни ошибка
    // каталога не должны блокировать всю страницу позиции: таб показывает их сам.
    // Настройки меню и категории гейтим: без них сохранение ушло бы с пустым списком
    // языков и категорий (та же причина, что и с константами).
    // Справочник бейджей гейтим вместе с остальными: без него чипы выбранных бейджей
    // отрисовались бы пустыми, а позиция выглядела бы как «бейджей нет».
    const loading = itemLoading || constantsLoading || menuLoading || categoriesLoading || badgesLoading;
    const error = itemError || constantsError || menuError || categoriesError || badgesError;

    // --- Локальное состояние позиции ---
    const [name, setName] = useState<Record<string, string>>({});
    const [description, setDescription] = useState<Record<string, string>>({});
    const [menuCategoryId, setMenuCategoryId] = useState<number>(categoryId);

    const [price, setPrice] = useState<number>(0);
    const [priceToShow, setPriceToShow] = useState<string>("");
    const [oldPrice, setOldPrice] = useState<number>(0);
    const [oldPriceToShow, setOldPriceToShow] = useState<string>("");
    const [isInStopList, setIsInStopList] = useState<boolean>(false);

    const [weight, setWeight] = useState<string | null>(null);
    const [measurement, setMeasurement] = useState<string | null>(null);
    const [calories, setCalories] = useState<number | null>(null);
    const [caloriesToShow, setCaloriesToShow] = useState<string>("");
    const [proteins, setProteins] = useState<number | null>(null);
    const [proteinsToShow, setProteinsToShow] = useState<string>("");
    const [fats, setFats] = useState<number | null>(null);
    const [fatsToShow, setFatsToShow] = useState<string>("");
    const [carbohydrates, setCarbohydrates] = useState<number | null>(null);
    const [carbohydratesToShow, setCarbohydratesToShow] = useState<string>("");

    const [cookingTimeFromCategory, setCookingTimeFromCategory] = useState<boolean>(false);
    const [individualCookingTime, setIndividualCookingTime] = useState<boolean>(false);
    const [itemCookingTime, setItemCookingTime] = useState<{ fromTime: number | null; toTime: number | null }>({
        fromTime: null,
        toTime: null,
    });

    // Порядок списка — порядок бейджей на карточке меню.
    const [badgeIds, setBadgeIds] = useState<number[]>([]);
    const [schedule, setSchedule] = useState<ScheduleDto | null>(null);
    const [media, setMedia] = useState<MediaItemDto[]>([]);
    const [modifierSets, setModifierSets] = useState<ModifierSetRefDto[]>([]);
    const [arCodeLink, setArCodeLink] = useState<string | null>(null);
    const [supplierLogoUrl, setSupplierLogoUrl] = useState<string | null>(null);
    const [externalId, setExternalId] = useState<string | null>(null);
    const [selectedExternalProduct, setSelectedExternalProduct] = useState<ExternalProductDto | null>(null);

    const notify = useNotificationSnackbar();
    const { saving: savingModifierSet, saveModifierSet } = useModifierSetMutations(
        modifierSetsCatalog,
        setModifierSetsCatalog,
        notify
    );

    const [saving, setSaving] = useState<boolean>(false);
    const [deleting, setDeleting] = useState<boolean>(false);

    const deleteItem = async () => {
        setDeleting(true);

        try {
            await deleteMenuItem(menuItemId);
            // Та же причина, что и при сохранении: сообщение показывается уже на экране меню.
            history.push(`/menus/regular/${menuId}/view`, notifyOnPage("Позиция удалена"));
        } catch (e) {
            notify.showError("Не удалось удалить позицию", e);
        } finally {
            setDeleting(false);
        }
    };

    // Инициализация из загруженной позиции. Ждём и валюту: цены форматируются по её decimalPlaces.
    useEffect(() => {
        if (!item || !currency) return;

        setName(item.name);
        setDescription(item.description);
        setMenuCategoryId(item.menuCategoryId);

        setPrice(item.sellPrice.price);
        setPriceToShow(currencyFormatter.toShow(item.sellPrice.price, true));
        setOldPrice(item.oldPrice?.price || 0);
        setOldPriceToShow(currencyFormatter.toShow(item.oldPrice?.price || 0, true));
        setIsInStopList(item.isInStopList);

        setWeight(item.weight.value);
        setMeasurement(item.weight.measurement || "г");
        setCalories(item.calories.calories);
        setCaloriesToShow(formatDecimalForInput(item.calories.calories));
        setProteins(item.calories.proteins);
        setProteinsToShow(formatDecimalForInput(item.calories.proteins));
        setFats(item.calories.fats);
        setFatsToShow(formatDecimalForInput(item.calories.fats));
        setCarbohydrates(item.calories.carbohydrates);
        setCarbohydratesToShow(formatDecimalForInput(item.calories.carbohydrates));

        setCookingTimeFromCategory(item.cookingTimeFromCategory);
        setItemCookingTime(item.itemCookingTime);
        setIndividualCookingTime(!item.cookingTimeFromCategory && !!item.itemCookingTime.fromTime);

        setBadgeIds(item.badgeIds || []);
        setSchedule(item.schedule);
        setMedia(item.media);
        setModifierSets(item.modifierSets);
        setArCodeLink(item.arCodeLink);
        setSupplierLogoUrl(item.supplierLogoUrl);
        setExternalId(item.externalId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item, currency]);

    // --- Обработчики ---
    const setPriceWrapper = createMoneyInputHandler(
        currencyFormatter.toSend, setPrice, setPriceToShow
    );

    const setOldPriceWrapper = createMoneyInputHandler(
        currencyFormatter.toSend, setOldPrice, setOldPriceToShow
    );

    const setCaloriesHandler = createDecimalInputHandler(setCalories, setCaloriesToShow);
    const setProteinsHandler = createDecimalInputHandler(setProteins, setProteinsToShow);
    const setFatsHandler = createDecimalInputHandler(setFats, setFatsToShow);
    const setCarbohydratesHandler = createDecimalInputHandler(setCarbohydrates, setCarbohydratesToShow);

    const setLocalizedName = (value: string, lang: string) =>
        setName((prev) => ({ ...prev, [lang]: value }));
    const setLocalizedDescription = (value: string, lang: string) =>
        setDescription((prev) => ({ ...prev, [lang]: value }));

    const addBadge = (badgeId: number) =>
        setBadgeIds((prev) => (prev.includes(badgeId) ? prev : [...prev, badgeId]));

    const removeBadge = (badgeId: number) =>
        setBadgeIds((prev) => prev.filter((id) => id !== badgeId));

    // Переставляем по id, а не по индексам: список чипов отфильтрован от бейджей, удалённых
    // из справочника, и его индексы не совпадают с индексами badgeIds.
    const moveBadge = (badgeId: number, targetBadgeId: number) =>
        setBadgeIds((prev) => {
            const from = prev.indexOf(badgeId);
            const to = prev.indexOf(targetBadgeId);

            if (from === -1 || to === -1) return prev;

            const next = [...prev];
            next.splice(from, 1);
            next.splice(to, 0, badgeId);
            return next;
        });

    const removeMedia = (index: number) =>
        setMedia((prev) => prev.filter((_item, i) => i !== index));

    const moveMedia = (index: number, direction: "up" | "down") => {
        const target = direction === "up" ? index - 1 : index + 1;
        if (target < 0 || target > media.length - 1) return;
        const next = [...media];
        [next[index], next[target]] = [next[target], next[index]];
        setMedia(next);
    };

    const addModifierSet = (menuItemModifierSetId: number) =>
        setModifierSets((prev) => [
            ...prev,
            { menuItemModifierSetId, sortOrder: prev.length },
        ]);

    const removeModifierSet = (index: number) =>
        setModifierSets((prev) => prev.filter((_item, i) => i !== index));

    const moveModifierSet = (index: number, direction: "up" | "down") => {
        const target = direction === "up" ? index - 1 : index + 1;
        if (target < 0 || target > modifierSets.length - 1) return;
        const next = [...modifierSets];
        [next[index], next[target]] = [next[target], next[index]];
        setModifierSets(next);
    };

    /*
    * Набор модификаторов — общий справочник заведения, поэтому сохраняется сразу, отдельно
    * от кнопки «Сохранить» позиции (та пишет только привязки и порядок). Мутации общие с
    * фичей наборов — тексты и обновление каталога не разъезжаются.
    * */

    const buildRequest = (): UpdateMenuItemRequestDto => ({
        menuCategoryId,
        name,
        description,
        sellPrice: { currencyId, price },
        oldPrice: { currencyId, price: oldPrice },
        isInStopList,
        weight: { value: weight, measurement },
        calories: { calories, proteins, carbohydrates, fats },
        cookingTimeFromCategory,
        itemCookingTime: individualCookingTime ? itemCookingTime : { fromTime: null, toTime: null },
        // В запрос не отдаём id, которых в справочнике уже нет: иначе мёртвая ссылка
        // сохранялась бы обратно в позицию.
        badgeIds: badgeIds.filter((badgeId) => badgesCatalog.some((badge) => badge.id === badgeId)),
        schedule: schedule as ScheduleDto,
        media,
        // Порядок наборов в карточке задаётся порядком списка — нумеруем по нему.
        modifierSets: modifierSets.map((ref, index) => ({ ...ref, sortOrder: index })),
        arCodeLink: arCodeLink && arCodeLink.trim() !== "" ? arCodeLink.trim() : null,
        supplierLogoUrl: supplierLogoUrl && supplierLogoUrl.trim() !== "" ? supplierLogoUrl.trim() : null,
        externalId,
    });

    const save = async () => {
        setSaving(true);
        try {
            await updateMenuItem(menuItemId, buildRequest());
            // Меню в сторе не перезагружаем: страница списка (/view) грузит его сама
            // при монтировании, а лишний полный getMenuById задерживал бы навигацию.
            //
            // Тост должен пережить уход со страницы, а состояние уведомлений фичи умирает
            // вместе с ней — поэтому сообщение едет состоянием роута.
            history.push(`/menus/regular/${menuId}/view`, notifyOnPage("Позиция сохранена"));
        } catch (e) {
            notify.showError("Не удалось сохранить позицию", e);
        } finally {
            setSaving(false);
        }
    };

    // Справочное о меню — из REST.
    const availableLanguages: string[] = menuSettings?.availableLanguages.enabledLocales || [];
    const parentCategory = categories.find((category) => category.id === categoryId);
    const hasIikoIntegration = menuSettings?.hasIikoIntegration || false;

    return {
        // meta
        menuId,
        menuItemId,
        loading,
        error,
        saving,
        deleting,
        deleteItem,
        locale,
        currencyShortName: currency?.shortName[locale] || "",
        // reference
        currencyFormatter,
        badgesCatalog,
        availableLanguages,
        categories,
        parentCategory,
        hasIikoIntegration,
        // localized text
        name,
        description,
        setLocalizedName,
        setLocalizedDescription,
        // basics
        menuCategoryId,
        setMenuCategoryId,
        priceToShow,
        setPriceWrapper,
        oldPriceToShow,
        setOldPriceWrapper,
        isInStopList,
        setIsInStopList,
        // weight / kbju
        weight,
        setWeight,
        measurement,
        setMeasurement,
        caloriesToShow,
        setCaloriesHandler,
        proteinsToShow,
        setProteinsHandler,
        fatsToShow,
        setFatsHandler,
        carbohydratesToShow,
        setCarbohydratesHandler,
        // cooking time
        cookingTimeFromCategory,
        setCookingTimeFromCategory,
        individualCookingTime,
        setIndividualCookingTime,
        itemCookingTime,
        setItemCookingTime,
        // badges
        badgeIds,
        addBadge,
        removeBadge,
        moveBadge,
        // schedule
        schedule,
        setSchedule,
        // media
        media,
        setMedia,
        removeMedia,
        moveMedia,
        // modifiers
        modifierSets,
        modifierSetsCatalog,
        modifierSetsLoading,
        modifierSetsError,
        addModifierSet,
        removeModifierSet,
        moveModifierSet,
        saveModifierSet,
        savingModifierSet,
        // extra
        arCodeLink,
        setArCodeLink,
        supplierLogoUrl,
        setSupplierLogoUrl,
        externalId,
        setExternalId,
        selectedExternalProduct,
        setSelectedExternalProduct,
        // уведомления фичи: свои, без стора
        notify,
        // actions
        save,
    };
};

export type MenuItemEditorState = ReturnType<typeof useMenuItemEditorState>;
